// decision-ledger-projection.ts — V2-1 sổ quyết định (spec V2 §4.1 "Sổ quyết
// định", bảng §5.1, nghiệm thu (12), (14), (15); Codex F3).
//
// Sổ = projection của journal ở control root, KHÔNG ghi repo (sống qua
// `reset --hard` + `clean -fd`, không commit metadata sau candidate freeze):
//  - mục máy: `decision_auto_applied` (nguồn `delegated:<G0 id>`, I6);
//  - mục người: `decision_answered` (nguồn `user:<id>`), dedupe theo
//    `(question_id, directive_id)` — event ghi lặp do crash chỉ là một mục;
//  - text câu hỏi + nhãn: `decision_question_asked` cùng `question_id`.
// interview.json KHÔNG phải nguồn của mục — chỉ là fallback text câu hỏi cho
// mission 3.34.3 (có `decision_answered`, chưa có `decision_question_asked`;
// mất file ⇒ hiện `question_id` + `value`) và bộ lọc "câu còn chờ" (câu đã bị
// thay theo luật V2-0 không còn chờ). Mục "worker ghi nhận" (E13
// `worker_decisions`) tách riêng, không phải quyết định auto/người. Mọi chuỗi
// hiển thị qua redactor (sink controller, I4a). Kiểu: decision-ledger-types.ts;
// hàm phụ: decision-ledger-projection-helpers.ts (review 03b L8).
import { join } from "node:path";
import {
  type AskedSnapshot,
  answeredLabel,
  askedSnapshot,
  clean,
  dropUndefined,
  pendingFromInterviewOnly,
  str,
  workerDecisionText,
} from "./decision-ledger-projection-helpers.js";
import type {
  DecisionLedger,
  DecisionLedgerEntry,
  PendingDecisionQuestion,
  WorkerRecordedDecision,
} from "./decision-ledger-types.js";
import { readInterview } from "./interview-store.js";
import { scanJournal } from "./journal-scan-report.js";
import { redactKnownValuesDeep } from "./known-secret-values-deep-display-redaction.js";
import { knownValuesForControlRoot } from "./mission-secrets-test-env-loader.js";
import { MISSION_JOURNAL_FILE_NAME } from "./mission-store.js";
import type { Interview } from "./schemas/interview-schema.js";

export type {
  DecisionLedger,
  DecisionLedgerEntry,
  PendingDecisionOption,
  PendingDecisionQuestion,
  WorkerRecordedDecision,
} from "./decision-ledger-types.js";

export function projectDecisionLedger(
  deltas: ReadonlyArray<Record<string, unknown>>,
  interview: Interview | undefined,
): DecisionLedger {
  const asked = new Map<string, AskedSnapshot>();
  const seenHumanKeys = new Set<string>();
  const rows: Array<Omit<DecisionLedgerEntry, "code">> = [];
  const workerRecorded: WorkerRecordedDecision[] = [];
  const questionText = (qid: string): string =>
    clean(asked.get(qid)?.summary || interview?.questions.find((q) => q.id === qid)?.title || qid);

  for (const delta of deltas) {
    const qid = str(delta.question_id);
    if (delta.kind === "decision_question_asked" && qid) {
      asked.set(qid, askedSnapshot(delta));
    } else if (delta.kind === "decision_auto_applied" && qid) {
      const optionId = str(delta.option_id);
      const label = str(delta.option_label) ?? optionId ?? "?";
      const authority = str(delta.authority_ref);
      rows.push({
        origin: "auto",
        questionId: qid,
        ticketId: str(delta.ticket_id) ?? asked.get(qid)?.ticketId,
        question: questionText(qid),
        optionId,
        optionLabel: clean(label),
        source: str(delta.actor) ?? `delegated:${authority ?? "?"}`,
        decidedAt: str(delta.decided_at),
      });
    } else if (delta.kind === "decision_answered" && qid) {
      const key = `${qid}\u0000${str(delta.directive_id) ?? ""}`;
      if (seenHumanKeys.has(key)) continue;
      seenHumanKeys.add(key);
      rows.push({
        origin: "human",
        questionId: qid,
        ticketId: str(delta.ticket_id) ?? asked.get(qid)?.ticketId,
        question: questionText(qid),
        optionId: str(delta.option_id),
        optionLabel: clean(answeredLabel(delta.value) ?? "?"),
        source: str(delta.actor) ?? "user:?",
        decidedAt: str(delta.decided_at),
      });
    } else if (delta.kind === "attempt_finished" && Array.isArray(delta.worker_decisions)) {
      const ticketId = str(delta.ticket) ?? "?";
      const attemptNo = typeof delta.attemptNo === "number" ? delta.attemptNo : undefined;
      for (const record of delta.worker_decisions) {
        workerRecorded.push({
          ticketId,
          ...(attemptNo === undefined ? {} : { attemptNo }),
          text: workerDecisionText(record),
        });
      }
    }
  }

  const entries = rows.map((row, i) =>
    dropUndefined({ code: `QĐ-${String(i + 1).padStart(2, "0")}`, ...row }),
  );
  const decided = new Set(entries.map((e) => e.questionId));
  const pending: PendingDecisionQuestion[] = [];
  for (const [questionId, snap] of asked) {
    if (decided.has(questionId)) continue;
    // Còn interview.json ⇒ chỉ câu vẫn còn đó và chưa có answer (câu bị thay không chờ nữa).
    if (interview !== undefined) {
      if (!interview.questions.some((q) => q.id === questionId)) continue;
      if (interview.answers[questionId] !== undefined) continue;
    }
    pending.push(
      dropUndefined({
        questionId,
        ticketId: snap.ticketId,
        question: clean(snap.summary || questionId),
        options: snap.options,
        recommendedOptionId: snap.recommendedOptionId,
      }),
    );
  }
  // M1: câu chỉ có trong interview.json (mission 3.34.x) — sau các câu journal đã hỏi.
  if (interview !== undefined) {
    pending.push(...pendingFromInterviewOnly(interview, new Set([...asked.keys(), ...decided])));
  }
  return { entries, workerRecorded, pending };
}

/** Sổ của một mission: journal ở control root (+ interview.json chỉ cho fallback/câu chờ). */
export async function readDecisionLedger(controlRoot: string): Promise<DecisionLedger> {
  const scan = await scanJournal(join(controlRoot, MISSION_JOURNAL_FILE_NAME));
  const interview = await readInterview(controlRoot);
  const ledger = projectDecisionLedger(
    scan.records.map((r) => r.delta),
    interview,
  );
  // Phase 04a: sổ là sink hiển thị (`status --decisions`, mô tả MR) — che
  // known-values của mission (registry) trên mọi chuỗi.
  return redactKnownValuesDeep(
    { ...ledger, journalStatus: scan.status },
    await knownValuesForControlRoot(controlRoot),
  );
}
